import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { api, ApiResponse } from "_/utils";
import { Uuid } from "_/types";

const SCOPE = "sso";
export const KEYS = {
  orgProviders: (orgId: Uuid) => [SCOPE, "providers", orgId],
};

export type SsoProviderConfig = {
  id: Uuid;
  organizationId: Uuid;
  providerType: "oidc" | "saml2";
  name: string;
  isEnabled: boolean;
  emailDomains: string[];
  createdAt: string;
  updatedAt: string;
};

export type SsoProviderConfigOidcView = {
  issuerUrl: string;
  clientId: string;
  scopes: string[];
  authMethod: string;
  claimEmail: string;
  claimSubject: string;
};

export type SsoProviderConfigSamlView = {
  idpEntityId: string;
  idpMetadataUrl: string;
  idpSsoUrl: string;
  certCount: number;
  attributeMap: Record<string, unknown>;
  nameIdFormat: string | null;
};

export type SsoProviderConfigView = SsoProviderConfig & {
  oidc?: SsoProviderConfigOidcView;
  saml?: SsoProviderConfigSamlView;
};

export type NewSsoProviderOidc = {
  type: "oidc";
  name: string;
  issuerUrl: string;
  clientId: string;
  clientSecret: string;
  scopes?: string[];
  authMethod?: string;
  claimEmail?: string;
  claimSubject?: string;
  emailDomains?: string[];
};

export type NewSsoProviderSaml = {
  type: "saml2";
  name: string;
  idpEntityId: string;
  idpMetadataUrl?: string;
  idpSsoUrl: string;
  idpCerts: string[];
  attributeMap?: Record<string, unknown>;
  nameIdFormat?: string;
  emailDomains?: string[];
};

export type NewSsoProvider = NewSsoProviderOidc | NewSsoProviderSaml;

export type SsoProviderOidcUpdate = {
  issuerUrl?: string;
  clientId?: string;
  clientSecret?: string;
};

export type SsoProviderSamlUpdate = {
  idpEntityId?: string;
  idpMetadataUrl?: string;
  idpSsoUrl?: string;
};

export type SsoProviderUpdate = {
  name?: string;
  isEnabled?: boolean;
  emailDomains?: string[];
  oidc?: SsoProviderOidcUpdate;
  saml?: SsoProviderSamlUpdate;
};

async function getProviders(orgId: Uuid): Promise<SsoProviderConfigView[]> {
  const response = await api.get<ApiResponse<SsoProviderConfigView[]>>(
    `/organizations/${orgId}/sso/providers`
  );
  return response.data.data;
}

async function createProvider(params: {
  orgId: Uuid;
  provider: NewSsoProvider;
}): Promise<SsoProviderConfigView> {
  const response = await api.post<ApiResponse<SsoProviderConfigView>>(
    `/organizations/${params.orgId}/sso/providers`,
    params.provider
  );
  return response.data.data;
}

async function testProvider(params: {
  orgId: Uuid;
  providerId: Uuid;
}): Promise<void> {
  await api.post<ApiResponse<unknown>>(
    `/organizations/${params.orgId}/sso/providers/${params.providerId}/test`
  );
}

async function updateProvider(params: {
  orgId: Uuid;
  providerId: Uuid;
  update: SsoProviderUpdate;
}): Promise<SsoProviderConfigView> {
  const response = await api.patch<ApiResponse<SsoProviderConfigView>>(
    `/organizations/${params.orgId}/sso/providers/${params.providerId}`,
    params.update
  );
  return response.data.data;
}

async function createProviderFromMetadata(params: {
  orgId: Uuid;
  name: string;
  metadataUrl: string;
  emailDomains?: string[];
}): Promise<SsoProviderConfigView> {
  const response = await api.post<ApiResponse<SsoProviderConfigView>>(
    `/organizations/${params.orgId}/sso/providers/from-metadata`,
    {
      name: params.name,
      metadataUrl: params.metadataUrl,
      emailDomains: params.emailDomains,
    }
  );
  return response.data.data;
}

async function deleteProvider(params: {
  orgId: Uuid;
  providerId: Uuid;
}): Promise<void> {
  await api.delete<ApiResponse<void>>(
    `/organizations/${params.orgId}/sso/providers/${params.providerId}`
  );
}

export function useSsoProviders(orgId: Uuid) {
  return useQuery({
    queryKey: KEYS.orgProviders(orgId),
    queryFn: () => getProviders(orgId),
  });
}

export function useCreateSsoProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProvider,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: KEYS.orgProviders(variables.orgId),
      });
    },
  });
}

export function useCreateSsoProviderFromMetadata() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProviderFromMetadata,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: KEYS.orgProviders(variables.orgId),
      });
    },
  });
}

export function useTestSsoProvider() {
  return useMutation({ mutationFn: testProvider });
}

export function useUpdateSsoProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProvider,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: KEYS.orgProviders(variables.orgId),
      });
    },
  });
}

export function useDeleteSsoProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProvider,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: KEYS.orgProviders(variables.orgId),
      });
    },
  });
}
